/* eslint-disable @typescript-eslint/camelcase */
import * as React from 'react';
import { useSelector } from 'react-redux';
import { get, getConfig, getText, getUpdatedLink } from '../../helpers';
import { ReactComponent as ThumbUpIcon } from '../../assets/svg/icons/thumb-up.svg';
import { ReactComponent as ThumbDownIcon } from '../../assets/svg/icons/thumb-down.svg';
import { ReactComponent as ThumbUpColorIcon } from '../../assets/svg/icons/thumb-up-filled.svg';
import { ReactComponent as SearchAllFilterIcon } from '../../assets/svg/icons/search-all-filter.svg';
import { ReactComponent as DatasetsFilterIcon } from '../../assets/svg/icons/datasets-filter.svg';
import { ReactComponent as SolutionsFilterIcon } from '../../assets/svg/icons/solutions-filter.svg';
import { ReactComponent as BlueprintFilterIcon } from '../../assets/svg/icons/blueprint-filter.svg';
import TileCarousel from '../_common/tile-carousel';
import CustomLink from '../_common/custom-link';

const Window = window as AnonymousObject;
Window.dataLayer = Window.dataLayer || [];
const searchErrorMessage = getText('pages.llm.errorMessage');
const langCode = getText('langCode');
const userGuideText = getText('pages.llm.userGuide');
const techGuideText = getText('pages.llm.techGuide');
const searchAllFilter = getText('globalSearchFilters.searchAllText');
const dataGuideText = getText('pages.llm.dataGuide');
const supportArticleText = getText('pages.llm.supportArticle');
const marketplaceFaqText = getText('pages.llm.marketplaceFaq');
const datasetsFilter = getText('globalSearchFilters.DatasetsText');
const solutionsFilter = getText('globalSearchFilters.SolutionsText');
const bluePrintFilter = getText('globalSearchFilters.BlueprintsText');
const slickOptions = getConfig('components.llmSearch.carouselOptions');
const aiPoweredText = getText('pages.llm.aiPoweredHomePgSearchText');
const learnMoreText = getText('pages.llm.learnMoreText');
const learnMoreUrl = getText('pages.llm.learnMoreUrl');

type RelatedLink = {
    link_category: string;
    name: string;
    url: string;
};

const GenAISearchResult = (props: GenAISearchResultProps) => {
    const { response, isLoading, handleFeedback, hasError, clearFeedbackFlag, clearFeedbackDoneNotify, parentRef, onTabPressed, source } = props;
    const [feedback, setFeedback] = React.useState<string>('');
    const [allResultList, setAllResultList] = React.useState<ResultProps[]>([]);
    const [searchFilter, setSearchFilter] = React.useState<string>('All Results');
    const [callToActionLinks, seCallToActionLinks] = React.useState<JSX.Element[]>([]);

    const user = useSelector((state: RootStoreState) => get(state, 'user', ''));
    const isLoggedIn = user && user.isLoggedIn ? user.isLoggedIn : false;
    const signInUrl = getConfig('urls.mi.ssacs.oktaSignIn');
    let returnUrl = '';

    const onLoginClick = (event: React.SyntheticEvent) => {
        if (process.env.NODE_ENV === 'production') {
            if (window.location.pathname.length > 1) {
                returnUrl = `&mpurl=${encodeURIComponent(window.location.pathname)}`;
            }
            event.preventDefault();
            window.location.href = `${signInUrl + returnUrl}`;
        } else {
            window.location.href = signInUrl;
        }
    };
    const signInToMarketPlaceButton = (
        <a
            href={signInUrl}
            target="_self"
            rel="noopener noreferrer"
            data-testid="hero-product-finder--login-link"
            className="gen-ai-search-result__link-group-btn gen-ai-search-result__sign-in-btn"
            onClick={onLoginClick}
            key="hero-product-finder--login-link"
        >
            <span>Sign In to Marketplace</span>
        </a>
    );
    const [nonLoggedInActionLinks, setNonLoggedInActionLinks] = React.useState<JSX.Element[]>([signInToMarketPlaceButton]);

    React.useEffect(() => {
        if (isLoading) {
            setFeedback('');
            setSearchFilter('All Results');
            setAllResultList([]);
        }
    }, [isLoading]);

    const populatFilterLinks = (valueList: any[]) => {
        const allRecords: ResultProps[] = [];
        if (valueList && valueList.length > 0) {
            valueList.forEach(item => {
                const record = Object.values(item)[0] as ResultProps;
                if (item.marketplaceDataset || item.marketplaceDatasetBundle) allRecords.push({ ...record, type: 'datasets' });
                if (item.marketplaceSolution) allRecords.push({ ...record, type: 'solutions' });
                if (item.marketplaceBlueprint) allRecords.push({ ...record, type: 'blueprints' });
            });
        }
        setAllResultList(allRecords);
    };

    const onLinkClicked = (link: string) => {
        if (link) {
            Window.dataLayer.push({
                event: 'suggestionButtonLLM',
                suggestionButtonURL: link,
            });
            window.open(link, '_blank');
        }
    };

    const responseVaidation = () => {
        if (response && response.related_links) {
            const actions: RelatedLink[] = []; // callToAction buttons objects
            const filteredLinks: RelatedLink[] = response.related_links.filter((link: any) => link.url !== undefined && link.link_category !== undefined); // valid links
            // get call-to-action links according to relevancy scores
            // filteredLinks are already ordered acoording to relevancy
            // we just need to get first object from each category and not loose order
            filteredLinks.forEach((link: RelatedLink) => {
                const cat = link.link_category;
                if ((cat === 'tile_user_guide' || cat === 'data_guide' || cat === 'knowledge_base' || cat === 'faq') && !actions.some(el => el.link_category === cat)) {
                    actions.push(link);
                }
            });

            const nonLoggedInActions: RelatedLink[] = [];
            filteredLinks.forEach((link: RelatedLink) => {
                if (link.link_category === 'faq' && !nonLoggedInActions.some(el => el.link_category === 'faq')) {
                    nonLoggedInActions.push(link);
                }
            });
            // call-to-action button element
            const actionButton = (key: string, url: string, name: string) => {
                return (
                    <button
                        type="button"
                        key={key}
                        data-testid="user-guide-btn"
                        className="gen-ai-search-result__link-group-btn"
                        onClick={() => onLinkClicked(url)}
                    >
                        <span>{name}</span>
                    </button>
                );
            };
            if (actions.length && isLoggedIn) {
                seCallToActionLinks(
                    actions.map((el: RelatedLink) => {
                        switch (el.link_category) {
                            case 'tile_user_guide':
                                return actionButton(el.link_category, el.url, (el.url.includes('/datasets/') || el.url.includes('/blueprints/')) ? userGuideText : techGuideText);
                            case 'data_guide':
                                return actionButton(el.link_category, el.url, dataGuideText);
                            case 'knowledge_base':
                                return actionButton(el.link_category, el.url, supportArticleText);
                            case 'faq':
                                return actionButton(el.link_category, el.url, marketplaceFaqText);
                            default:
                                return <></>;
                        }
                    })
                );
            }
            if (nonLoggedInActions.length) {
                const newLinks = nonLoggedInActions.map((el: RelatedLink) => {
                    switch (el.link_category) {
                        case 'faq':
                            return actionButton(el.link_category, el.url, marketplaceFaqText);
                        default:
                            return <></>;
                    }
                });
                setNonLoggedInActionLinks([signInToMarketPlaceButton, ...newLinks]);
            } else {
                setNonLoggedInActionLinks([signInToMarketPlaceButton]);
            }
        }

        const validLinks = response && response.related_links ? response.related_links.filter((link: any) => link.url === undefined) : [];
        populatFilterLinks(validLinks && validLinks.length > 0 ? validLinks.slice(0, validLinks.length > 20 ? 20 : validLinks.length) : []);
    };

    React.useEffect(() => {
        responseVaidation();
    }, [response]);

    React.useEffect(() => {
        if (clearFeedbackFlag && feedback !== '') {
            setFeedback('');
            if (clearFeedbackDoneNotify) clearFeedbackDoneNotify();
        }
    }, [clearFeedbackFlag]);

    const sendFeedback = (value: string) => {
        const responseId = response && response.response_uuid;
        if (handleFeedback) handleFeedback(responseId, value);
    };

    const onThumbUpButtonClick = () => {
        setFeedback('positive');
        sendFeedback('positive');
    };

    const onThumbDownButtonClick = () => {
        setFeedback('negative');
        sendFeedback('negative');
    };

    const getThumbUpIcon = () => {
        if (feedback === 'positive') return <ThumbUpColorIcon aria-label="thumb up selected" />;
        return <ThumbUpIcon data-testid="thumb-up-btn" aria-label="thumb up not selected" />;
    };

    const getThumbDownIcon = () => {
        if (feedback === 'negative') return <ThumbUpColorIcon className="gen-ai-search-result__thumbup_rotate" aria-label="thumb down selected" />;
        return <ThumbDownIcon data-testid="thumb-down-btn" aria-label="thumb down not selected" />;
    };

    const filterBtnIconFill = (isActive: boolean) => {
        return isActive ? '#D6002A' : '#515151';
    };

    const getCount = (value: string) => {
        if (value === 'All Results') return allResultList.length;
        const count = allResultList.filter(item => value.toLowerCase() === item.type);
        return count.length;
    };

    const handleFilterChanged = (filterState: string) => {
        setSearchFilter(filterState);
        const scrollableWrapper = document.getElementsByClassName('gen-ai-search-result__searchfilterbar')[0];
        if (filterState === 'All Results') {
            scrollableWrapper.scrollLeft -= 100;
        }
        if (filterState === 'Blueprints') {
            scrollableWrapper.scrollLeft += 100;
        }
    };

    const handleKeyEvent = (event: React.KeyboardEvent) => {
        if (event.keyCode === 8) {
            // const element = document.getElementById('data-search-bar-btn');
            // if (element) element.focus();
        }
    };

    const keyPressHandler = (event: KeyboardEvent) => {
        if (event.key === 'Tab') {
            let itemId = '';
            if (event.target instanceof Element) {
                itemId = event.target.getAttribute('id') || '';
            }
            if (hasError) {
                if (itemId === '' && event.target instanceof Element) {
                    itemId = event.target.getAttribute('data-testid') || '';
                }
                if (itemId === 'learnMoreLink') {
                    event.preventDefault();
                    if (onTabPressed) onTabPressed('gen-ai-result-end');
                }
            } else {
                if (itemId && itemId === 'blueprint-btn' && !event.shiftKey) {
                    const count = getCount('Blueprints');
                    if (count === 0) {
                        event.preventDefault();
                        if (onTabPressed) onTabPressed('gen-ai-result-end');
                    }
                }
                if (itemId === '') {
                    const eventTarget = event.target as HTMLElement;
                    const parent = eventTarget.parentElement;
                    const ns = parent && parent.nextElementSibling;
                    if (parent && parent.parentElement && ((parent.parentElement.className.includes('slick-slide') && !parent.parentElement.nextElementSibling) || (parent.parentElement.className === 'slick-dots' && !ns))) {
                        const sdElement = document.getElementsByClassName('slick-dots');
                        if (!(parent.parentElement.className.includes('slick-slide') && sdElement && sdElement.length > 0)) {
                            event.preventDefault();
                            if (onTabPressed) onTabPressed('gen-ai-result-end');
                        }
                    }
                }
                if (itemId && itemId === 'typeahead-blueprintsLink' && !event.shiftKey) {
                    event.preventDefault();
                    if (onTabPressed) onTabPressed('gen-ai-result-end');
                }
            }
        }
    };

    React.useEffect(() => {
        const container = parentRef ? parentRef.current : null;
        if (container) container.addEventListener('keydown', keyPressHandler);
        return () => {
            if (container) {
                container.removeEventListener('keydown', keyPressHandler);
            }
        };
    });

    const filterOptionAllresult = () => {
        return (
            <>
                <div className="gen-ai-search-result__searchfilterbar">
                    <div role="button" id="all-result-btn" className={getCount('All Results') === 0 ? 'gen-ai-search-result__disabled' : 'gen-ai-search-result__enabled'} onClick={() => handleFilterChanged('All Results')} onFocus={() => { handleFilterChanged('All Results'); }} onKeyUp={e => { handleKeyEvent(e); }} tabIndex={0} data-testid="search-all">
                        <div className={`${searchFilter === 'All Results' ? 'gen-ai-search-result__searchfilterbtn__enable icon' : 'gen-ai-search-result__searchfilterbtn__inactive icon'}`}>
                            <SearchAllFilterIcon className="icon" fill={filterBtnIconFill(searchFilter === 'All Results')} />
                            <div className="gen-ai-search-result__searchfilterbar__searchFiltersText">
                                {searchAllFilter} {getCount('All Results')}
                            </div>
                        </div>
                    </div>
                    <div role="button" id="dataset-btn" className={getCount('Datasets') === 0 ? 'gen-ai-search-result__disabled' : 'gen-ai-search-result__enabled'} onClick={() => handleFilterChanged('Datasets')} onFocus={() => { handleFilterChanged('Datasets'); }} onKeyUp={e => { handleKeyEvent(e); }} tabIndex={0} data-testid="search-dataset">
                        <div className={`${searchFilter === 'Datasets' ? 'gen-ai-search-result__searchfilterbtn__enable icon' : 'gen-ai-search-result__searchfilterbtn__inactive icon'}`}>
                            <DatasetsFilterIcon className="icon" fill={filterBtnIconFill(searchFilter === 'Datasets')} />
                            <div className="gen-ai-search-result__searchfilterbar__searchFiltersText">
                                {datasetsFilter} {getCount('Datasets')}
                            </div>
                        </div>
                    </div>
                    <div role="button" id="solution-btn" className={getCount('Solutions') === 0 ? 'gen-ai-search-result__disabled' : 'gen-ai-search-result__enabled'} onClick={() => handleFilterChanged('Solutions')} onFocus={() => { handleFilterChanged('Solutions'); }} onKeyUp={e => { handleKeyEvent(e); }} tabIndex={0} data-testid="search-solution">
                        <div className={`${searchFilter === 'Solutions' ? 'gen-ai-search-result__searchfilterbtn__enable icon' : 'gen-ai-search-result__searchfilterbtn__inactive icon'}`}>
                            <SolutionsFilterIcon className="icon" fill={filterBtnIconFill(searchFilter === 'Solutions')} />
                            <div className="gen-ai-search-result__searchfilterbar__searchFiltersText">
                                {solutionsFilter} {getCount('Solutions')}
                            </div>
                        </div>
                    </div>
                    <div role="button" id="blueprint-btn" className={getCount('Blueprints') === 0 ? 'gen-ai-search-result__disabled' : 'gen-ai-search-result__enabled'} onClick={() => handleFilterChanged('Blueprints')} onFocus={() => { handleFilterChanged('Blueprints'); }} onKeyUp={e => { handleKeyEvent(e); }} tabIndex={0} data-testid="search-blueprint">
                        <div className={`${searchFilter === 'Blueprints' ? 'gen-ai-search-result__searchfilterbtn__enable icon' : 'gen-ai-search-result__searchfilterbtn__inactive icon'}`}>
                            <BlueprintFilterIcon className="icon" fill={filterBtnIconFill(searchFilter === 'Blueprints')} />
                            <div className="gen-ai-search-result__searchfilterbar__searchFiltersText">
                                {bluePrintFilter} {getCount('Blueprints')}
                            </div>
                        </div>
                    </div>
                </div>
                <div className="gen-ai-search-result__searchfilterbar--a" />
            </>
        );
    };

    const getRecordPerType = (value: string) => {
        const records = allResultList.filter(item => item.type && item.type === value.toLowerCase());
        return records.map(el => el.id);
    };

    const getTileIds = () => {
        if (searchFilter === 'All Results') return allResultList.map(el => el.id);
        return getRecordPerType(searchFilter);
    };

    return (
        <div
            data-testid="gen-ai-search-result"
            className={source && source === 'global' ? 'gen-ai-search-result__from-global' : 'gen-ai-search-result'}
        >
            {isLoading && (
                <div className="gen-ai-search-result__search_loading">
                    <div className="gen-ai-search-result__loading-text" role="alert">
                        Generating...
                    </div>
                    <div className="gen-ai-search-result__loading_bar" />
                    <div className="gen-ai-search-result__loading_bar" />
                    <div className="gen-ai-search-result__loading_bar" />
                </div>
            )}
            {!isLoading && ((response && response.response && response.response.length > 0) || hasError) && (
                <>
                    <div className="gen-ai-search-result__result-panel">
                        <div className="gen-ai-search-result__result-summary" role="alert">
                            {hasError ? searchErrorMessage : response.response.replace('\n1', '\n\n1')}
                        </div>
                    </div>
                    {response && response.response && response.response.length > 0 && (
                        <div
                            className={
                                callToActionLinks.length || !isLoggedIn || nonLoggedInActionLinks.length
                                    ? 'gen-ai-search-result__button-link-group'
                                    : 'gen-ai-search-result__thumb-up-down-group-only'
                            }
                        >
                            <div>{isLoggedIn ? callToActionLinks : nonLoggedInActionLinks}</div>
                            <div className="gen-ai-search-result__thumb-up-down-group">
                                <button type="button" className="gen-ai-search-result__thumb-up-icon" onClick={onThumbUpButtonClick}>
                                    {getThumbUpIcon()}
                                </button>
                                <button type="button" className="gen-ai-search-result__thumb-down-icon" onClick={onThumbDownButtonClick}>
                                    {getThumbDownIcon()}
                                </button>
                            </div>
                        </div>
                    )}
                    <div className="gen-ai-search-result__aipowertitle">
                        <span className="gen-ai-search-result__aipowertext">{aiPoweredText}</span>
                        <CustomLink
                            id="learnMoreLink"
                            className="gen-ai-search-result__aipowertext__link"
                            hasIcon
                            text={learnMoreText}
                            hasUnderline
                            url={getUpdatedLink(learnMoreUrl, langCode)}
                        />
                    </div>
                    {response && response.response && response.response.length > 0 && (
                        <>
                            <hr className="gen-ai-search-result__topline" />
                            <div className="gen-ai-search-result__button-link-group-addl">{filterOptionAllresult()}</div>
                            <hr className="gen-ai-search-result__bottomline" />
                            {allResultList && allResultList.length > 0 && (
                                <div className="gen-ai-search-result__carousel">
                                    <TileCarousel
                                        dataIds={getTileIds()}
                                        slickOptions={slickOptions}
                                        fullBleed
                                        newTab
                                        source="genai"
                                        carouselCompKey={searchFilter}
                                    />
                                </div>
                            )}
                        </>
                    )}
                </>
            )}
        </div>
    );
};

export default GenAISearchResult;
